import { createServerFn } from "@tanstack/react-start"
import { and, eq, sql } from "drizzle-orm"
import { z } from "zod"

import { DetectionFeedbackReason } from "@/constants/terminal/detection-feedback-reason"
import {
  type DetectionFeedbackAnswer,
  DetectionFeedbackVote,
  reasonsByDetectionFeedbackVote,
} from "@/constants/terminal/detection-feedback-vote"
import { terminalDetectionFeedback } from "@/db/schema/terminal-detection-feedback"
import { terminalDetection } from "@/db/schema/terminal-file"
import {
  isCurrentPortfolioEntry,
  terminalPortfolioEntry,
} from "@/db/schema/terminal-portfolio"
import { db } from "@/lib/admin-db"
import { assertPortfolioAccess } from "@/lib/supabase/auth"

// A member's answers to Detections. Like every customer Terminal server
// function, each handler first calls `assertPortfolioAccess()` with the
// Portfolio it names; a member reads and writes only their own answers.

const portfolioInput = z.object({ portfolioSlug: z.string().min(1) })

// The caller's own answers to the Portfolio's Detections, by durable
// Detection id. Nobody sees another member's. The /terminal loader shows no vote when
// this read fails, as on a database without the detection_feedback table.
export const loadTerminalDetectionFeedback = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => portfolioInput.parse(data))
  .handler(
    async ({ data }): Promise<Record<string, DetectionFeedbackAnswer>> => {
      const { portfolioId, userId } = await assertPortfolioAccess(data)
      const rows = await db
        .select({
          detectionId: terminalDetectionFeedback.detection_id,
          vote: terminalDetectionFeedback.vote,
          reason: terminalDetectionFeedback.reason,
        })
        .from(terminalDetectionFeedback)
        .where(
          and(
            eq(terminalDetectionFeedback.portfolio_id, portfolioId),
            eq(terminalDetectionFeedback.user_id, userId)
          )
        )
      return Object.fromEntries(
        rows.map(({ detectionId, vote, reason }) => [
          detectionId,
          { vote, reason: reason ?? null },
        ])
      )
    }
  )

// Records the caller's answer to one Detection the Portfolio can read (its
// Company has a Current Portfolio Entry there): the vote and, when given, a
// reason that vote offers. Answering again replaces it. The answer follows
// the Detection across every Assessment, since its id is durable.
export const answerTerminalDetection = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    portfolioInput
      .extend({
        detectionId: z.string().uuid(),
        vote: z.nativeEnum(DetectionFeedbackVote),
        reason: z.nativeEnum(DetectionFeedbackReason).nullable(),
      })
      .refine(
        ({ vote, reason }) =>
          reason === null ||
          reasonsByDetectionFeedbackVote[vote].includes(reason),
        { message: "That reason does not belong to this vote" }
      )
      .parse(data)
  )
  .handler(async ({ data }): Promise<void> => {
    const { portfolioId, userId } = await assertPortfolioAccess(data)
    const { detectionId, vote, reason } = data
    const [detection] = await db
      .select({ id: terminalDetection.id })
      .from(terminalDetection)
      .innerJoin(
        terminalPortfolioEntry,
        and(
          eq(terminalPortfolioEntry.company_id, terminalDetection.company_id),
          eq(terminalPortfolioEntry.portfolio_id, portfolioId),
          isCurrentPortfolioEntry
        )
      )
      .where(eq(terminalDetection.id, detectionId))
      .limit(1)
    if (!detection) throw new Error("No such Detection in this Portfolio")
    await db
      .insert(terminalDetectionFeedback)
      .values({
        user_id: userId,
        portfolio_id: portfolioId,
        detection_id: detectionId,
        vote,
        reason,
      })
      .onConflictDoUpdate({
        target: [
          terminalDetectionFeedback.user_id,
          terminalDetectionFeedback.portfolio_id,
          terminalDetectionFeedback.detection_id,
        ],
        set: { vote, reason, updated_at: sql`now()` },
      })
  })
